import { useState, type ReactNode } from 'react';
import { Modal } from '../../ui/kit.tsx';
import type { AiMode } from './types.ts';

/** The one confirmation Studio asks for: destructive actions (revoke, remove). Plain edits save straight away. */
export function ConfirmButton({
  label,
  title,
  children,
  confirmLabel,
  onConfirm,
  className = 'btn btn-small btn-danger-ghost',
  disabled = false,
}: {
  label: ReactNode;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  onConfirm: () => Promise<unknown> | void;
  className?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)} disabled={disabled}>
        {label}
      </button>
      {open ? (
        <Modal
          title={title}
          onClose={() => setOpen(false)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={confirm} disabled={busy} autoFocus>
                {confirmLabel}
              </button>
            </>
          }
        >
          {children}
        </Modal>
      ) : null}
    </>
  );
}

export const AI_MODE_LABELS: Record<AiMode, string> = { off: 'Off', suggest: 'Suggest (a person approves)', auto: 'Automatic' };

export const CAPABILITY_LABELS: Record<string, string> = {
  'seo_text.generate': 'SEO text: write',
  'seo_text.seasonal_update': 'SEO text: seasonal updates',
  'seo_text.rewrite': 'SEO text: rewrite',
  'meta.title': 'Meta title',
  'meta.description': 'Meta description',
  faq: 'FAQ',
  'text_link.insert': 'Text links in copy',
  'coupon.create': 'Coupons: create',
  'coupon.edit': 'Coupons: edit',
  'coupon.expire': 'Coupons: expire',
  'coupon.ordering': 'Coupons: order',
  translation: 'Translation',
  'image.alt': 'Image alt text',
};

/** "" in a form field means "not set" (inherit / remove). */
export const blankToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export function since(iso: string | null): string {
  if (!iso) return 'never';
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}
